import axios from 'axios';
import { LoginCredentials, SignupCredentials, AuthResponse, AuthError } from '../types/auth';
import { handleApiError, isNetworkError, formatValidationErrors } from '../utils/errorHandler';

const API_URL = process.env.REACT_APP_API_URL || '';
console.log('API_URL:', API_URL);

type AuthDebugDetails = Record<string, unknown>;

const AUTH_DEBUG_STORAGE_KEY = 'stockit_auth_debug_log';

const getSafeLocalStorageItem = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const setSafeLocalStorageItem = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures; console logging still works.
  }
};

const headersToObject = (headers: any): Record<string, unknown> => {
  if (!headers) return {};
  if (typeof headers.toJSON === 'function') return headers.toJSON();
  return { ...headers };
};

const authClientDebug = (event: string, details: AuthDebugDetails = {}) => {
  const entry = {
    timestamp: new Date().toISOString(),
    event,
    pageOrigin: window.location.origin,
    pageHref: window.location.href,
    apiUrl: API_URL,
    online: navigator.onLine,
    userAgent: navigator.userAgent,
    details,
  };

  // Keep logs on the client because CORS/preflight failures may never reach the backend.
  console.info('[AUTH_CLIENT_DEBUG]', entry);

  const previous = getSafeLocalStorageItem(AUTH_DEBUG_STORAGE_KEY);
  let log: unknown[] = [];
  try {
    log = previous ? JSON.parse(previous) : [];
    if (!Array.isArray(log)) log = [];
  } catch {
    log = [];
  }
  log.push(entry);
  setSafeLocalStorageItem(AUTH_DEBUG_STORAGE_KEY, JSON.stringify(log.slice(-100)));
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    authClientDebug('window:error', {
      message: event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: any = event.reason;
    authClientDebug('window:unhandledrejection', {
      message: reason?.message || String(reason),
      code: reason?.code,
      name: reason?.name,
      stack: reason?.stack,
    });
  });

  (window as any).dumpAuthDebugLog = () => {
    const log = getSafeLocalStorageItem(AUTH_DEBUG_STORAGE_KEY) || '[]';
    try {
      const parsed = JSON.parse(log);
      console.table(parsed);
      return parsed;
    } catch {
      console.warn('[AUTH_CLIENT_DEBUG] stored log could not be parsed', log);
      return [];
    }
  };

  (window as any).clearAuthDebugLog = () => {
    setSafeLocalStorageItem(AUTH_DEBUG_STORAGE_KEY, '[]');
    console.info('[AUTH_CLIENT_DEBUG] cleared');
  };
}

class AuthService {
  private token: string | null = null;
  private interceptorId: number | null = null;
  private isHandlingUnauthorized = false;

  constructor() {
    // Initialize token from localStorage if it exists
    this.token = localStorage.getItem('token');
  }

  private setToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  private clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  getAuthHeader() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private async withAuthDebug<T>(label: string, request: () => Promise<T>, details: AuthDebugDetails = {}): Promise<T> {
    const startedAt = Date.now();
    authClientDebug(`${label}:start`, details);

    try {
      const response: any = await request();
      authClientDebug(`${label}:success`, {
        ...details,
        durationMs: Date.now() - startedAt,
        status: response?.status,
        statusText: response?.statusText,
        responseHeaders: headersToObject(response?.headers),
      });
      return response;
    } catch (error: any) {
      authClientDebug(`${label}:error`, {
        ...details,
        durationMs: Date.now() - startedAt,
        message: error?.message,
        code: error?.code,
        name: error?.name,
        isAxiosError: Boolean(error?.isAxiosError),
        hasResponse: Boolean(error?.response),
        hasRequest: Boolean(error?.request),
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        responseHeaders: headersToObject(error?.response?.headers),
        responseData: error?.response?.data,
        requestUrl: error?.config?.url,
        requestMethod: error?.config?.method,
        requestHeaders: {
          ...headersToObject(error?.config?.headers),
          Authorization: error?.config?.headers?.Authorization ? '[present-redacted]' : undefined,
        },
      });
      throw error;
    }
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await this.withAuthDebug(
        'login',
        () => axios.post<AuthResponse>(
          `${API_URL}/api/auth/login`,
          credentials
        ),
        {
          endpoint: `${API_URL}/api/auth/login`,
          method: 'POST',
          hasEmail: Boolean(credentials.email),
          hasPassword: Boolean(credentials.password),
          contentType: 'application/json',
        }
      );
      
      this.setToken(response.data.token);
      return response.data;
    } catch (error) {
      authClientDebug('login:handled-network-error', {
        endpoint: `${API_URL}/api/auth/login`,
      });
      if (isNetworkError(error)) {
        throw new AuthError(
          'Unable to connect to the server. Please check your internet connection.',
          'NETWORK_ERROR'
        );
      }

      const apiError = handleApiError(error);
      const validationErrors = apiError.status === 422 
        ? formatValidationErrors((error as any).response?.data?.errors)
        : undefined;
      
      throw new AuthError(apiError.message, apiError.code, validationErrors);
    }
  }

  async signup(credentials: SignupCredentials): Promise<AuthResponse> {
    try {
      const response = await this.withAuthDebug(
        'signup',
        () => axios.post<AuthResponse>(
          `${API_URL}/api/auth/register`,
          credentials
        ),
        {
          endpoint: `${API_URL}/api/auth/register`,
          method: 'POST',
          hasEmail: Boolean(credentials.email),
          hasPassword: Boolean(credentials.password),
          contentType: 'application/json',
        }
      );
      
      this.setToken(response.data.token);
      return response.data;
    } catch (error) {
      authClientDebug('signup:handled-network-error', {
        endpoint: `${API_URL}/api/auth/register`,
      });
      if (isNetworkError(error)) {
        throw new AuthError(
          'Unable to connect to the server. Please check your internet connection.',
          'NETWORK_ERROR'
        );
      }

      const apiError = handleApiError(error);
      const validationErrors = apiError.status === 422 
        ? formatValidationErrors((error as any).response?.data?.errors)
        : undefined;
      
      throw new AuthError(apiError.message, apiError.code, validationErrors);
    }
  }

  async logout(): Promise<void> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        this.clearToken();
        return;
      }

      await this.withAuthDebug(
        'logout',
        () => axios.post(
          `${API_URL}/api/auth/logout`,
          {},
          {
            headers: this.getAuthHeader(),
            // Prevent interceptor recursion if logout itself returns 401
            _skipAuthInterceptor: true as any,
          } as any
        ),
        {
          endpoint: `${API_URL}/api/auth/logout`,
          method: 'POST',
          hasToken: Boolean(token),
        }
      );
    } catch (error) {
      // Even if logout fails on the server, we still want to clear local state
      console.error('Logout error:', handleApiError(error).message);
    } finally {
      this.clearToken();
    }
  }

  async validateToken(): Promise<boolean> {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      await this.withAuthDebug(
        'validateToken',
        () => axios.get(`${API_URL}/api/auth/validate-token`, {
          headers: this.getAuthHeader()
        }),
        {
          endpoint: `${API_URL}/api/auth/validate-token`,
          method: 'GET',
          hasToken: Boolean(token),
        }
      );
      return true;
    } catch (error) {
      if (!isNetworkError(error)) {
        // Only clear token if it's an auth error, not a network error
        this.clearToken();
      }
      return false;
    }
  }

  async getMe(): Promise<AuthResponse['user']> {
    try {
      const response = await this.withAuthDebug(
        'getMe',
        () => axios.get<{ user: AuthResponse['user'] }>(
          `${API_URL}/api/auth/me`,
          { headers: this.getAuthHeader() }
        ),
        {
          endpoint: `${API_URL}/api/auth/me`,
          method: 'GET',
          hasToken: Boolean(localStorage.getItem('token')),
        }
      );
      return response.data.user;
    } catch (error) {
      throw new Error('Failed to fetch user profile');
    }
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    return !!token;
  }

  setupAxiosInterceptors(onSessionExpired: () => void): void {
    if (this.interceptorId !== null) {
      axios.interceptors.response.eject(this.interceptorId);
    }

    this.interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const requestConfig = error.config || {};
        const requestUrl = requestConfig.url || '';
        const skipInterceptor = !!requestConfig._skipAuthInterceptor;

        // Ignore logout endpoint and explicitly skipped requests to prevent loops
        if (skipInterceptor || requestUrl.includes('/api/auth/logout')) {
          return Promise.reject(error);
        }

        if ((status === 401 || status === 403) && !this.isHandlingUnauthorized) {
          this.isHandlingUnauthorized = true;

          this.logout().finally(() => {
            onSessionExpired();
            this.isHandlingUnauthorized = false;
          });
        }

        return Promise.reject(error);
      }
    );
  }
}

const authService = new AuthService();
export default authService;
